// ─── Hex board — shared geometry & style constants ────────────────────────────
//
// Everything the hex board's layers size themselves against, in BOARD UNITS:
// the SVG's viewBox is BOARD_W × BOARD_H and the whole board scales as one
// picture to fit its container (see HexBoard's fit), so these never change at
// runtime. R is the cell's circumradius at the reference width (375px, 21
// columns), H its height. Data-level hex maths lives in utils/hexGrid.js and
// the lattice layout in utils/hexGeometry.js; this file is the board's own
// derived layout (tile boxes, lanes, carets, shadows) plus the colours copied
// from the retired square board's BoardGrid.css / BattlefieldCell.css, each
// annotated with its source.

import { LAYOUT, COLOR } from '../../../config/uiTokens'
import { radiusForWidth, hexHeight, boardSize, hexPoints, halfEdgeY } from '../../../utils/hexGeometry'

export const R  = radiusForWidth(LAYOUT.SCREEN_W)   // 375 / 32
export const H  = hexHeight(R)
export const { width: BOARD_W, height: BOARD_H } = boardSize(R)

// The subplot-model sides render in this order (opp on top, self below).
export const SIDES = ['opp', 'self']

// .bfc--opp / .bfc--player — one colour per half, hosting rows and no-fly rows
// alike (the no-fly zone isn't different ground; the lanes say where drones go).
export const LAND = {
    opp:  { fill: COLOR.CELL_OPP },
    self: { fill: COLOR.CELL_PLAYER },
}
// .bg__ps-cell hairline: inset 0 0 0 0.5px rgba(0,0,0,0.08)
export const HAIRLINE = 'rgba(0, 0, 0, 0.08)'

// An inset ring of width w: a hex shrunk so the stroke's outer edge lands on
// the cell edge (the CSS rings are inset box-shadows).
export const ringPoints = (cx, cy, w) => hexPoints(cx, cy, R * (1 - w / H))

// ── Plot tiles ──────────────────────────────────────────────────────────────
// The box a plot's drone tile fills: one corridor pitch (4.5R) wide, centred
// on the corridor's middle column, three rows tall. Every corridor's stack
// sits at the SAME distance from the band — a quarter hex, the midpoint
// between where an even and an odd middle column's first row would put it —
// so drones (and their lanes) line up in rows across the board instead of
// stepping up and down with column parity.
export const TILE_LEAD = H / 4
export function plotTileBox(side, c, p) {
    const left = 4.5 * R * c + 0.25 * R
    const d0 = 3 * H * p + TILE_LEAD                  // near edge, from the band
    const near = side === 'self' ? halfEdgeY('self', R) + d0 : halfEdgeY('opp', R) - d0
    const top = side === 'self' ? near : near - 3 * H
    return { left, top, width: 4.5 * R, height: 3 * H, cx: left + 2.25 * R, cy: top + 1.5 * H }
}

// A drone tile's circle is 80% of the tile box's smaller side (.ab__wrap--round
// 80cqmin); the box is 4.5R × 3H and 4.5R is the smaller, so the diameter is 3.6R.
export const DRONE_RADIUS = 1.8 * R

// ── Lanes (airspace) ────────────────────────────────────────────────────────
// A lane is a capsule on the drones' own axis: its centreline runs from the
// front-most slot's centre to the back-most's, radius = the drone's plus
// LANE_PAD, so it hugs the stack symmetrically at both ends and sides.
// LANE_PAD is capped by the corridor pitch (4.5R ≈ 52.7px): the capsule's
// width plus its stroke must clear the neighbouring capsule — at 2 the
// capsules sit ~5px apart edge to edge (4 touched, 2.5 nearly did).
export const LANE_PAD = 2
export const LANE_RADIUS = DRONE_RADIUS + LANE_PAD
// The opponent's airspace reads at half strength — it's their capacity, not
// yours to fill, so it stays secondary to your own lanes.
export const LANE_OPP_OPACITY = 0.5
// The lane's defined edge: a thin stroke in a lighter tint of the haze's
// blue-white, moderate opacity so it reads on tan and red land alike.
export const LANE_STROKE   = 'rgba(228, 240, 255, 0.38)'
export const LANE_STROKE_W = 1.5
// An armed (attacking) lane: the same edge in the attack red.
export const LANE_STROKE_ARMED = 'rgba(255, 130, 130, 0.55)'
// A lane a dragged drone is about to land in: a subtle amber edge (the
// player amber, COLOR.ARMY_PLAYER) — and, the moment the move lands, the
// whole lane blinks once in transparent amber (see .hexb__lane-flash).
export const LANE_STROKE_HOVER = 'rgba(212, 134, 10, 0.6)'
export const LANE_FLASH_FILL   = 'rgba(212, 134, 10, 0.5)'
export const LANE_FLASH_STROKE = 'rgba(212, 134, 10, 0.95)'

// ── Attack caret ────────────────────────────────────────────────────────────
// The caret on a lane's band-end cap: its base is an ARC of the cap itself
// (same radius, same centre), so it sits on the pill's edge as the pill's own
// point, apex reaching into the band. Dragging it CARET_DRAG_PX toward the
// enemy arms the corridor; back the other way disarms.
export const CARET_HALF_W  = 8
export const CARET_H       = 7
export const CARET_DRAG_PX = 10
export const CARET_LIFT_MAX = 8      // how far the caret visibly follows the finger
// The badge's attack red, armed or not — the armed state reads from the
// reddened lane and the vanished chevrons, not from the point itself.
export const CARET_FILL      = COLOR.BTN_ATTACK      // #e05050
// The caret's shadow offset, cast north into the band.
export const CARET_SHADOW_DY = 1.5
// "Slide to attack": above an UNARMED caret, two open chevrons of the same
// pitch step up and fade — the swipe-up affordance. Each rides on a blurred
// dark halo so it reads on the band AND on the opp land it climbs into.
export const CHEVRON_STEP    = 6
export const CHEVRON_OPACITY = [0.9, 0.55]
export const CHEVRON_W       = 1.8
export const CHEVRON_HALO_W  = CHEVRON_W + 3

// ── Drone ground shadow ─────────────────────────────────────────────────────
// Cast well below the tile and smaller than it, the way a high-flying
// object's shadow lands — sells the altitude over the land.
export const SHADOW_DROP = 18                    // px below the tile centre
export const SHADOW_RX   = 0.72 * DRONE_RADIUS   // a touch wider than tall
export const SHADOW_RY   = 0.62 * DRONE_RADIUS

// ── Ground drones ───────────────────────────────────────────────────────────
// A fresh deploy (kind 'green') isn't flying yet: it stands ON the land,
// drawn smaller than the airborne tiles and sunk a little toward where its
// shadow falls, with a tight faint shadow right under it — as much shadow
// as a thing on the ground casts. At submit it lifts off (useTokenFlip):
// grows to flying size as the altitude shadow fades in beneath it.
export const GROUND_SCALE          = 0.68
export const GROUND_SINK           = 5                    // board units down from the tile centre
export const GROUND_SHADOW_DROP    = 2.5
export const GROUND_SHADOW_RX      = 0.62 * DRONE_RADIUS * GROUND_SCALE
export const GROUND_SHADOW_RY      = 0.40 * DRONE_RADIUS * GROUND_SCALE
export const GROUND_SHADOW_OPACITY = 0.32

// ── Airdrop arc ─────────────────────────────────────────────────────────────
// A drop zone's crate launches from its Base and lobs over to the zone. On
// this top-down board altitude reads as screen-UP (the drones' shadows fall
// below them), so the lob is a quadratic curve from the Base's bar centre to
// the slot's centre whose control point is lifted straight up: the crest
// (t = 0.5, where the chute opens) sits ARC_LIFT above the straight ground
// track's midpoint, the track being where its shadow runs — and always at
// least ARC_CLEAR above the higher of the two ends, so a steep hop (a Base
// deep in the Grid feeding a slot up by the band) still reads as a lob and
// not a slide. The lift grows with the distance, within bounds, so a zone
// over its own Base still visibly lobs and a far one doesn't leave the
// board — and on YOUR side it never rises past the band (arcCeiling): an
// airdrop stays over your own land, so a hop between two points up by the
// band flattens rather than crossing into the enemy's half. (The
// opponent's lobs rise toward the top edge, away from the band.)
export const ARC_LIFT_MIN  = 2.2 * H
export const ARC_LIFT_MAX  = 6 * H
export const ARC_LIFT_RATE = 0.34
export const ARC_CLEAR     = H
export const ARC_BAND_AIR  = 4                   // how far into the band a self lob may reach
export const arcCeiling = (side) => (side === 'self' ? halfEdgeY('self', R) - ARC_BAND_AIR : -Infinity)
export function airdropArc(from, to, { ceilingY = -Infinity } = {}) {
    const d = Math.hypot(to.x - from.x, to.y - from.y)
    const lift = Math.max(ARC_LIFT_MIN, Math.min(ARC_LIFT_MAX, ARC_LIFT_RATE * d))
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
    const top = Math.min(from.y, to.y)
    // Under the ceiling the crest keeps at least a hair above the higher end.
    const crestY = Math.min(mid.y - lift, top - ARC_CLEAR) < ceilingY
        ? Math.min(Math.max(ceilingY, Math.min(top - ARC_CLEAR / 3, mid.y)), top - 1)
        : Math.min(mid.y - lift, top - ARC_CLEAR)
    // The curve passes halfway from the chord's midpoint to the control
    // point at t = 0.5, so the control point sits twice as far up as the crest.
    const ctrl = { x: mid.x, y: 2 * crestY - mid.y }
    const at = (t) => ({
        x: (1 - t) * (1 - t) * from.x + 2 * (1 - t) * t * ctrl.x + t * t * to.x,
        y: (1 - t) * (1 - t) * from.y + 2 * (1 - t) * t * ctrl.y + t * t * to.y,
    })
    return {
        from, to, ctrl, lift, dist: d,
        crest: { x: mid.x, y: crestY },
        at,
        path: `M ${from.x} ${from.y} Q ${ctrl.x} ${ctrl.y} ${to.x} ${to.y}`,
    }
}

// ── Gestures ────────────────────────────────────────────────────────────────
// A pointer is a TAP until it travels GESTURE_SLOP px. From then on: a drag
// that started on a movable drone is a PICK-UP — the tile follows the finger
// freely, like a chess piece, and on release snaps to the corridor under it
// if that's a legal landing (the neighbour for a move, home for an undo) or
// back where it was; anywhere else on the self half it's the ATTACK swipe
// (mostly vertical: up to arm, down to disarm, SWIPE_ARM_PX so a sloppy
// sideways brush never arms). A lifted tile scales up a touch.
export const GESTURE_SLOP    = 8
export const SWIPE_ARM_PX    = 28
export const PICKUP_SCALE    = 1.08
// The corridor a board x falls in (0–6): corridors are one pitch (4.5R) wide.
export const corridorAt = (x) => Math.max(0, Math.min(LAYOUT.GRID_COLS - 1, Math.floor(x / (4.5 * R))))

// ── Intel peek ──────────────────────────────────────────────────────────────
// Hold the header eye: every live bearing lights the ring of cells at its
// distance, hue-matched to its BUILDING (same letter, same colour) —
// BoardGrid's PEEK_RING_COLORS, verbatim.
export const PEEK_RING_COLORS = ['#ffdd5a', '#4dd8ff', '#ff5ad0', '#6aff9e', '#ff8a3d', '#b8a6ff', '#ff6a5a', '#5affd8']
export const letterColorIndex = (letter) => {
    let n = 0
    for (const ch of letter) n = n * 26 + (ch.charCodeAt(0) - 64)
    return n - 1
}

// Client → board coordinates for a pointer event on the board's SVG.
export function boardPoint(e, svg) {
    const rect = svg.getBoundingClientRect()
    return {
        x: (e.clientX - rect.left) * (BOARD_W / rect.width),
        y: (e.clientY - rect.top)  * (BOARD_H / rect.height),
    }
}
